import React, { useCallback } from 'react'
import { useIntl } from 'react-intl'

import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import noop from 'lib/function/noop'

interface Props {
  variation: 'trip' | 'leTripItem' | 'customTripItem' | 'exploreItem'
  onConfirmAddDates?: () => void
}

export function AddToItineraryDatesRequiredModal({
  variation,
  onConfirmAddDates = noop,
}: Props) {
  const intl = useIntl()
  const modalElement = useModalElementContext<boolean>()
  const onClickAddDates = useCallback(() => {
    modalElement?.resolve(true)
    onConfirmAddDates()
  }, [modalElement, onConfirmAddDates])

  const onClickCancel = useCallback(() => {
    modalElement?.resolve(false)
  }, [modalElement])

  let detailText: string
  switch (variation) {
    case 'trip':
      detailText = intl.formatMessage({
        id: 'IdCMMmQ4qb',
        defaultMessage: 'You need to add dates to your trip before you can add items to your itinerary.',
        description: 'Message explaining trip dates are required before adding items to itinerary',
      })
      break
    case 'leTripItem':
      detailText = intl.formatMessage({
        id: 'rFP3CQK+ck',
        defaultMessage: 'To include this item in your itinerary, enter your preferred dates on the offer page. These dates will then be saved with your offer.',
        description: 'Message explaining how to add dates for a booking item to include in itinerary',
      })
      break
    case 'customTripItem':
      detailText = intl.formatMessage({
        id: 'Rs0WrDGvWB',
        defaultMessage: 'To include this item in your itinerary, add your preferred dates. These dates will then be saved with your item.',
        description: 'Message explaining how to add dates for a custom item to include in itinerary',
      })
      break
    case 'exploreItem':
      detailText = intl.formatMessage({
        id: 'x5nO/98nsn',
        defaultMessage: 'You currently don’t have dates for your trip. Add dates to your trip settings to start building your itinerary and saving attractions.',
        description: 'Message explaining trip dates are needed to save attractions from explore',
      })
      break
  }

  return (
    <ModalBase>
      <ModalHeader title={intl.formatMessage({
        id: 'k41knSEbaW',
        defaultMessage: 'Move to',
        description: 'Modal title for moving a trip item to itinerary',
      })}
      />
      <ModalBody>
        <ModalContent>
          <BodyTextBlock variant="large">{detailText}</BodyTextBlock>
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          children: intl.formatMessage({
            id: 'ZWBio7LVK4',
            defaultMessage: 'Add dates',
            description: 'Button to confirm adding dates to trip for itinerary',
          }),
          onClick: onClickAddDates,
        }}
        secondaryActionProps={{
          children: intl.formatMessage({
            id: 'OOCrcRukwr',
            defaultMessage: 'Cancel',
            description: 'Button to cancel adding dates to trip',
          }),
          onClick: onClickCancel,
        }}
      />
    </ModalBase>
  )
}
